import React, { useEffect, useRef, useState } from 'react';
import SpeciesAutocomplete from './SpeciesAutocomplete';
import { createLakeGuidanceBatch } from '../../services/stewardshipService';

const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 };
const labelStyle = { display: 'grid', gap: 4, fontSize: 13 };

export default function BulkLakeGuidance({ tenantId, sourceDataset, lakes, onSaved, onClear, onBusyChange, canApprove = false, featureNoun = 'lakes' }) {
  const labels = Object.fromEntries(Object.entries(actionLabels).map(([key, label]) => [key, label.replace(/lakes/g, featureNoun)]));
  const [form, setForm] = useState({ speciesId: '', speciesName: '', score: 0, summary: '', reason: '', season: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [operation, setOperation] = useState('create');
  const [moreActions, setMoreActions] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [success, setSuccess] = useState('');
  const retry = useRef(null);
  const selectionLabel = `${lakes.length} ${lakes.length === 1 ? featureNoun.replace(/s$/, '') : featureNoun}`;
  useEffect(() => { setConfirmed(false); setSuccess(''); }, [lakes]);
  const change = (patch) => { setForm((current) => ({ ...current, ...patch })); setConfirmed(false); };
  const save = async (event) => {
    event.preventDefault();
    if (busy) return;
    const lakeOnly = ['approve_lakes', 'archive_lakes'].includes(operation);
    if (!lakeOnly && !form.speciesId) { setError('Choose a recognized species.'); return; }
    if (operation !== 'create' && !confirmed) { setError('Confirm the bulk action before applying it.'); return; }
    const payload = {
      sourceDataset, sourceFeatureIds: lakes.map((lake) => String(lake.id)),
      speciesId: form.speciesId, score: Number(form.score), summary: form.summary, reason: form.reason, season: form.season,
      operation,
    };
    const fingerprint = JSON.stringify(payload);
    if (retry.current?.fingerprint !== fingerprint) {
      retry.current = {
        fingerprint, requestId: Array.from(window.crypto.getRandomValues(new Uint32Array(4)), (n) => n.toString(16).padStart(8, '0')).join(''),
      };
    }
    setBusy(true); onBusyChange(true); setError(''); setSuccess('');
    try {
      const response = await createLakeGuidanceBatch(tenantId, { ...payload, requestId: retry.current.requestId });
      retry.current = null;
      setConfirmed(false);
      setSuccess(`Completed ${labels[operation].toLowerCase()} for ${response.data.lakeCount} ${featureNoun}. Selection retained.`);
      onSaved(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Could not save the guidance. Your selection is preserved; retry safely.');
    } finally { setBusy(false); onBusyChange(false); }
  };
  return (
    <form aria-label={`Guidance for selected ${featureNoun}`} onSubmit={save} style={{ margin: '16px 0', padding: 16, border: '1px solid #bfdbfe', borderRadius: 8 }}>
      <h3>{selectionLabel} selected</h3>
      <p>{lakes.map((lake) => lake.name).join(', ')}</p>
      <ol aria-label="Steps to publish guidance">
        <li><strong>Save a species draft.</strong> Save the score and notes for the chosen species and season. Only staff can see drafts.</li>
        <li><strong>Confirm the boundaries.</strong> Check that these are the areas you intend to use. Do this before publishing; an already confirmed boundary can be reused for other species.</li>
        <li><strong>Publish to harvesters.</strong> Make the saved species guidance visible on their map.</li>
      </ol>
      <p>Boundary confirmation is not a statement that harvesting is safe or permitted. Confirming a boundary alone does not publish guidance.</p>
      <p>Each action applies to all selected areas. Species actions affect only the species and season chosen below.</p>
      {error && <p role="alert">{error}</p>}
      {success && <p role="status">{success}</p>}
      <fieldset disabled={busy} style={{ border: 0, padding: 0, display: 'grid', gap: 12 }}>
        {canApprove && <button type="button" aria-expanded={moreActions} onClick={() => {
          setMoreActions(!moreActions); setOperation(moreActions ? 'create' : 'archive_guidance'); setConfirmed(false); setError(''); setSuccess('');
        }}>{moreActions ? 'Back to save and publish' : 'More actions: hide guidance or areas'}</button>}
        <label style={labelStyle}>Bulk action <select style={inputStyle} value={operation}
          onChange={(e) => { setOperation(e.target.value); setConfirmed(false); setError(''); setSuccess(''); }}>
          {Object.entries(labels).filter(([key]) => moreActions ? key.startsWith('archive_') : !key.startsWith('archive_'))
            .filter(([key]) => canApprove || ['create', 'edit_draft'].includes(key))
            .map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select></label>
        <p>{actionHelp[operation].replace(/lake/g, featureNoun === 'lakes' ? 'lake' : 'area')}</p>
        {!['approve_lakes', 'archive_lakes'].includes(operation) && <>
        <SpeciesAutocomplete canCreate={canApprove} value={form.speciesName} onSelect={(species) => change({ speciesId: species.speciesId || '', speciesName: species.speciesName })}
          onInputChange={(speciesName) => change({ speciesId: '', speciesName })} placeholder={`Species for all selected ${featureNoun}`} />
        {['create', 'edit_draft'].includes(operation) && <>
        <label style={labelStyle}>Score <input style={inputStyle} type="number" min="-10" max="10" step="1" required value={form.score} onChange={(e) => change({ score: e.target.value })} /></label>
        <label style={labelStyle}>Guidance for citizens <textarea style={inputStyle} rows={2} maxLength={5000} value={form.summary} onChange={(e) => change({ summary: e.target.value })} /></label>
        <label style={labelStyle}>Internal reason <textarea style={inputStyle} rows={2} maxLength={10000} value={form.reason} onChange={(e) => change({ reason: e.target.value })} /></label>
        </>}
        <label style={labelStyle}>Season <select style={inputStyle} value={form.season} onChange={(e) => change({ season: e.target.value })}>
          <option value="">All seasons</option>{['spring', 'summer', 'fall', 'winter'].map((season) => <option key={season}>{season}</option>)}
        </select></label>
        </>}
        {operation !== 'create' && <label>
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          {` Apply “${labels[operation]}” to ${selectionLabel}${['approve_lakes', 'archive_lakes'].includes(operation) ? '' : ` for ${form.speciesName || 'the chosen species'} (${form.season || 'all seasons'})`}.`}
        </label>}
        <div style={{ marginTop: 12 }}>
          <button type="submit">{busy ? 'Applying...' : `${labels[operation]} for ${selectionLabel}`}</button>
          <button type="button" onClick={onClear}>Clear selection</button>
        </div>
      </fieldset>
    </form>
  );
}

const actionLabels = {
  create: 'Save new species draft',
  edit_draft: 'Update species draft',
  approve_lakes: 'Confirm boundaries',
  publish: 'Publish to harvesters',
  archive_guidance: 'Hide species guidance',
  archive_lakes: 'Hide lakes and all their guidance',
};
const actionHelp = {
  create: 'Use this for a new species and season assignment. Saves the score and notes for staff review; harvesters cannot see them yet. Existing guidance is not changed. To replace existing guidance, choose Update species draft.',
  edit_draft: 'Replaces the score and notes for this species and season on every selected area, creating missing assignments too. Warning: currently published guidance becomes staff-only immediately and must be published again. Boundaries and evidence are preserved.',
  approve_lakes: 'Confirms the selected lake boundaries for stewardship use, copying untracked boundaries from the source first. Already confirmed boundaries can be reused. This does not publish guidance, approve a species, or declare harvesting safe.',
  publish: 'Shows the saved guidance for this species and season to harvesters. Every selected boundary must be confirmed and have saved species guidance. Uses saved values only, not unsaved form changes.',
  archive_guidance: 'Archives guidance for this species and season so harvesters no longer see it. Other species and the boundaries remain unchanged. Nothing is permanently deleted.',
  archive_lakes: 'Archives the selected lake records and hides all their species guidance from harvesters. The original source dataset is unchanged. Nothing is permanently deleted.',
};
